import { useEffect, useState, type FC } from 'react';
import { ShipmentDetailsCard } from './Components/ShipmentDetailsCard';
import ShipmentHeader from './Components/ShipmentCard/ShipmentHeader/ShipmentHeader';
import {
  DTOgetOrderDetailedData,
  IShipmentData,
  ORDER_STATUS_NAME,
  PAYMENTS_PROVIDER,
  ISelectedShipmentData,
  TEMPORARY_STORAGE_REASON_TYPE_ID,
  SHIPMENT_STATUS,
  ORDER_STATUS,
  SHIPMENT_STATUS_ID
} from '../../../../../types/orders';
import { BordOneToneIcon, NudosSelectButton } from '../../../../../components/NudosComponents';
import useOrdersState from '../../../../../state/useOrdersState';
import BordFloatIsland from '../../../../../components/BordFloatIsland/BordFloatIsland';
import { WarehouseTemporaryStorageModal } from './Components/ShipmentCard/WarehouseTemporaryStorageModal/WarehouseTemporaryStorageModal';
import { TemporaryStorageDropdown } from './Components/ShipmentCard/WarehouseTemporaryStorageButton/TemporaryStorageDropdown/TemporaryStorageDropdown';
import { LOCATION_PLACE_TYPE } from 'types/services';
import AddSerialStopInWarehouseModal from 'views/orders/Components/AddSerialStopInWarehouseModal/AddSerialStopInWarehouseModal';
import './Tab4Address.scss';

interface Tab4AddressProps {
  orderDetailedData?: DTOgetOrderDetailedData;
  updateWithoutLoaders: () => Promise<void>;
  updateWithLoaders: () => Promise<void>;
}

const Tab4Address: FC<Tab4AddressProps> = ({ orderDetailedData, updateWithoutLoaders, updateWithLoaders }) => {
  const { generalData, orderId, statusMethod, statusName, listProducts } = orderDetailedData ?? {};
  const {
    selectedShipmentIds,
    selectedProductIds,
    isShipmentToWarehouse,
    toggleAllShipmentsSelection,
    clearAllSelections,
    hasShipmentsWithDifferentPlaces,
    hasShipmentsWithDifferentTemporaryStorage,
    selectedShipmentsData
  } = useOrdersState();

  const [isEnabledChangeStatusCards, setIsEnabledChangeStatusCards] = useState<boolean>(false);
  const [showWarehouseTemporaryStorageModal, setShowWarehouseTemporaryStorageModal] = useState<boolean>(false);
  const [showNewModalAddSerialStopInWarehouse, setShowNewModalAddSerialStopInWarehouse] = useState<boolean>(false);
  const [showTemporaryStorageDropdown, setShowTemporaryStorageDropdown] = useState<boolean>(false);
  const [selectedReasonsForModal, setSelectedReasonsForModal] = useState<TEMPORARY_STORAGE_REASON_TYPE_ID[]>([]);

  const orderStatus = statusName?.toLocaleLowerCase().trim();
  const orderIsActive = orderStatus === ORDER_STATUS.ACTIVE.toLowerCase() || orderStatus === ORDER_STATUS_NAME.ACTIVE;
  const references = Object.values(listProducts || {})?.[0]?.references;

  const groupByShipmentGroupId = (shipmentData: IShipmentData[]) => {
    if (!shipmentData) return [];
    const grouped: Record<string, IShipmentData[]> = {};
    shipmentData?.forEach(shipment => {
      if (!grouped[shipment?.shipmentGroupId]) {
        grouped[shipment?.shipmentGroupId] = [];
      }
      grouped[shipment?.shipmentGroupId].push(shipment);
    });
    return Object.values(grouped);
  };
  // Los envíos se agrupan por Shipment GroupId para poder ponerlos en el mismo componente.
  const groupShipmentsByShipmentGroupId = groupByShipmentGroupId(generalData?.shipmentData || []);

  const totalProducts = selectedShipmentsData?.length || 0;

  const allProductIds =
    groupShipmentsByShipmentGroupId?.flatMap(shipmentGroup => {
      return shipmentGroup.flatMap(shipment => shipment.products?.map(p => p.id) || []);
    }) || [];

  const allShipmentsData: ISelectedShipmentData[] =
    groupShipmentsByShipmentGroupId?.flatMap(shipmentGroup => {
      return shipmentGroup
        .filter(shipment => shipment.place !== undefined && shipment.place !== LOCATION_PLACE_TYPE.NUDOS)
        .map(shipment => ({
          ...shipment,
          place: shipment.place as string,
          hasTemporaryStorage: !!shipment.warehouseTemporaryStorage?.estimatedEntryDate
        }));
    }) || [];

  const allShipmentsDataNudos = allShipmentsData?.every(shipment => shipment?.place === LOCATION_PLACE_TYPE.NUDOS);
  const someShipmentsDataConfirmed = allShipmentsData?.some(
    shipment => shipment?.statusId === SHIPMENT_STATUS_ID.CONFIRMED
  );

  const totalSelectedItems = totalProducts;
  const hasSelections = totalSelectedItems > 0;

  const getFirstSelectedShipmentId = () => {
    if (selectedShipmentIds.length > 0) {
      return selectedShipmentIds[0];
    }
    if (groupShipmentsByShipmentGroupId && groupShipmentsByShipmentGroupId.length > 0) {
      return groupShipmentsByShipmentGroupId[0][0]?.shipmentId;
    }
    return null;
  };

  const getFirstSelectedShipmentData = () => {
    if (selectedShipmentsData.length > 0) {
      return selectedShipmentsData[0];
    }
    return undefined;
  };

  const firstSelectedShipmentId = getFirstSelectedShipmentId();
  const firstSelectedShipmentData = getFirstSelectedShipmentData();

  const hasAnySelection = selectedShipmentIds?.length > 0 || totalSelectedItems > 0;

  const hasMixedPlaces = hasShipmentsWithDifferentPlaces();

  const listOfGroupedShipments = groupShipmentsByShipmentGroupId?.flatMap(listOfGroupedShipments => {
    return listOfGroupedShipments;
  });

  const temporaryStorageStatus = hasShipmentsWithDifferentTemporaryStorage();

  const renderIsButtonDisabled = () => {
    if (hasMixedPlaces?.onlyShipmentToWarehouse)
      return {
        message: 'No hay envios en bodega para aplicar acciones masivas.',
        isButtonDisabled: true
      };

    if (temporaryStorageStatus?.hasMixedTemporaryStorage)
      return {
        message: 'Selecciona envios con condiciones iguales para aplicar acciones masivas.',
        isButtonDisabled: true
      };

    if (hasMixedPlaces?.isShipmentToWarehouse || isShipmentToWarehouse)
      return {
        message: 'Selecciona envios con condiciones iguales para aplicar acciones masivas.',
        isButtonDisabled: true
      };

    return {
      message: temporaryStorageStatus.allHaveTemporaryStorage ? 'De confirmado a bodega' : 'Parada en bodega',
      isButtonDisabled: false
    };
  };

  const reloadDataCallback = async () => {
    await updateWithLoaders();
    await clearAllSelections();
  };

  const filterlistOfShipmentsAvailableToAdd = () => {
    const filterByTemporaryStorage = temporaryStorageStatus?.allHaveTemporaryStorage
      ? listOfGroupedShipments.filter(shipment => shipment?.warehouseTemporaryStorage?.estimatedEntryDate)
      : listOfGroupedShipments?.filter(shipment => !shipment?.warehouseTemporaryStorage?.estimatedEntryDate);

    return filterByTemporaryStorage
      ?.filter(shipment => shipment?.place !== LOCATION_PLACE_TYPE.NUDOS)
      ?.filter(
        shipment => shipment?.status !== SHIPMENT_STATUS.CANCELLED && shipment?.status !== SHIPMENT_STATUS.DELIVERED
      );
  };

  const handleOnButtonClick = () => {
    if (hasAnySelection) {
      if (temporaryStorageStatus?.allHaveTemporaryStorage) {
        setShowNewModalAddSerialStopInWarehouse(true);
      } else if (temporaryStorageStatus?.noneHaveTemporaryStorage) {
        setShowTemporaryStorageDropdown(true);
      }
    }
  };

  const handleCloseModalCallback = () => {
    setShowWarehouseTemporaryStorageModal(false);
    setSelectedReasonsForModal([]);
  };

  const handleRenderButtonIcon = () => {
    if (temporaryStorageStatus.allHaveTemporaryStorage) return <></>;
    if (temporaryStorageStatus.noneHaveTemporaryStorage) {
      return (
        <BordOneToneIcon
          variant={showTemporaryStorageDropdown ? 'arrowHeadUp' : 'arrowHeadDown'}
          customWidth="15px"
          stroke="currentColor"
        />
      );
    }
    return <></>;
  };

  const handleOnCloseFloatingIsland = () => {
    clearAllSelections();
    setShowTemporaryStorageDropdown(false);
    setShowWarehouseTemporaryStorageModal(false);
    setShowNewModalAddSerialStopInWarehouse(false);
  };

  useEffect(() => {
    if (
      (orderIsActive && statusMethod === PAYMENTS_PROVIDER) ||
      (orderIsActive && generalData?.paymentData?.proofOfPayment) ||
      (orderIsActive && generalData?.paymentData?.committedPaymentDate)
    ) {
      setIsEnabledChangeStatusCards(true);
    }
  }, [orderIsActive, generalData, statusMethod]);

  useEffect(() => {
    if (!hasSelections) {
      setShowTemporaryStorageDropdown(false);
    }
  }, [hasSelections]);

  return (
    <div className="tab4Address">
      <div className="trackingTitleContainer">
        <div className="trackingTitle">Tracking de envíos</div>
        <div className="selectAllShipmentsContainer">
          {!allShipmentsDataNudos && someShipmentsDataConfirmed && (
            <>
              <NudosSelectButton
                isButtonActive={
                  allProductIds?.length > 0 && allProductIds?.every(id => selectedProductIds.includes(id))
                }
                handleClickButton={() =>
                  toggleAllShipmentsSelection(
                    allProductIds,
                    allShipmentsData?.filter(s => s?.statusId === SHIPMENT_STATUS_ID.CONFIRMED)
                  )
                }
                componentSize="xs"
                customClassName="w-3 h-3"
              />
              <span className="selectAllShipmentsText">Seleccionar todos los envíos</span>
            </>
          )}
        </div>
      </div>
      {groupShipmentsByShipmentGroupId &&
        groupShipmentsByShipmentGroupId?.length > 0 &&
        groupShipmentsByShipmentGroupId?.map((listOfGroupedShipments, index) => {
          // datos que comparten todos los grupos de envíos
          const generalDataOfTheGroups = listOfGroupedShipments[0];
          return (
            <div
              key={`shipping-card-${generalDataOfTheGroups?.shipmentGroupId}-index-${index}`}
              className="shippingContainer"
            >
              <div className="flex flex-col">
                <ShipmentHeader
                  shipment={generalDataOfTheGroups}
                  orderDetailedData={orderDetailedData}
                  updateWithoutLoaders={updateWithoutLoaders}
                  listOfGroupedShipments={listOfGroupedShipments}
                  updateWithLoaders={updateWithLoaders}
                  hideDestinationCheckbox={true}
                  showExtrasMenu={true}
                  references={references}
                />
              </div>
              <div className="shipmentDetailsCardContainer">
                {generalData && (
                  <ShipmentDetailsCard
                    generalData={generalData}
                    listOfGroupedShipments={listOfGroupedShipments}
                    isEnabled={isEnabledChangeStatusCards}
                    updateWithoutLoaders={updateWithoutLoaders}
                    updateWithLoaders={updateWithLoaders}
                    orderId={String(orderId)}
                    listProducts={listProducts}
                  />
                )}
              </div>
            </div>
          );
        })}
      <div className="relative">
        {hasSelections && (
          <BordFloatIsland
            leftContentText="Productos seleccionados"
            leftContentCount={totalSelectedItems}
            buttonText={renderIsButtonDisabled()?.message}
            onClose={handleOnCloseFloatingIsland}
            onButtonClick={handleOnButtonClick}
            isButtonDisabled={renderIsButtonDisabled()?.isButtonDisabled}
            buttonProps={{
              colorPalette: 'transparent'
            }}
            buttonIcon={handleRenderButtonIcon()}
          />
        )}

        {showTemporaryStorageDropdown && firstSelectedShipmentId && (
          <div className="temporaryStorageDropdownContainer">
            <TemporaryStorageDropdown
              shipmentId={firstSelectedShipmentId}
              continueCallback={reasons => {
                setSelectedReasonsForModal(reasons);
                setShowTemporaryStorageDropdown(false);
                setShowWarehouseTemporaryStorageModal(true);
              }}
              initialReasons={[]}
              reloadDataCallback={updateWithLoaders}
              closeDropdownCallback={() => setShowTemporaryStorageDropdown(false)}
            />
          </div>
        )}

        {showWarehouseTemporaryStorageModal && firstSelectedShipmentId && (
          <WarehouseTemporaryStorageModal
            shipmentId={firstSelectedShipmentId}
            listOfShipmentsAvailableToAdd={filterlistOfShipmentsAvailableToAdd()}
            listOfSelectedShipments={selectedShipmentsData}
            references={references}
            orderId={String(orderId)}
            organizationName={generalData?.orgData?.organizationName}
            receiverName={firstSelectedShipmentData?.receiverInfo?.receiverName}
            flag={firstSelectedShipmentData?.country?.flag}
            initialValues={{}}
            reasonsForTemporaryStorage={selectedReasonsForModal}
            closeModalCallback={handleCloseModalCallback}
            reloadDataCallback={reloadDataCallback}
          />
        )}

        {showNewModalAddSerialStopInWarehouse && selectedShipmentsData?.length > 0 && (
          <AddSerialStopInWarehouseModal
            organizationName={generalData?.orgData?.organizationName || ''}
            orderId={String(orderId)}
            selectedShipments={selectedShipmentsData}
            setShowAddSerialStopInWarehouseModal={setShowNewModalAddSerialStopInWarehouse}
            updateWithoutLoaders={reloadDataCallback}
            references={references}
          />
        )}
      </div>
    </div>
  );
};

export default Tab4Address;
